import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { authData, saveAuth } from "../util/auth";
import { ApiResults, CartItemDTO, FileAttachment, OrderDTO, OrderHistoryDTO, ServiceResponse, ShopItemsDTO, ShopOrderPostDTO } from "./api-interface";
import { errorHandler } from "../util/errorHandler";
import { of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ApiService {

  constructor(private httpClient: HttpClient) {}

  baseUrl = `${environment.BASE_URL}/`
  loadingEndpoints: number = 0;
  postingEndpoints: number = 0;
  notification = {};

  clearNotification() {
    this.notification = {};
  }

  get<T>(uri: string, requiresAuth: boolean = true) : Promise<void | ServiceResponse<T>> {
    let httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: requiresAuth ? "Bearer " + authData().Token : "",
      }),
    };
    this.loadingEndpoints += 1;
    const baseUrl = `${this.baseUrl}${uri}`;

    let result = this.httpClient.get<ServiceResponse<T>>(baseUrl, httpOptions).toPromise()
      .catch((err) => { this.notification = errorHandler(err); })
      .finally(() => {this.postingEndpoints -= 1;});

    return result;
  }

  post<T,U>(uri: string, data: U, requiresAuth: boolean = true) : Promise<void | ServiceResponse<T>> {
    let httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: requiresAuth ? "Bearer " + authData().Token : "",
      }),
    };
    this.postingEndpoints += 1;
    const baseUrl = `${this.baseUrl}${uri}`;

    let result = this.httpClient.post<ServiceResponse<T>>(baseUrl, data, httpOptions).toPromise()
      .catch((err) => { this.notification = errorHandler(err); })
      .finally(() => {this.postingEndpoints -= 1;});

    return result;
  }

  getResponseData<T>(response: ServiceResponse<T>, isPosting: boolean = false): T {
    if (!response.Success) {
      this.notification = errorHandler(response);
    }
    if (response.Success && isPosting) {
      this.notification = {
        error: false,
        message: response.Data ||
          response.Message ||
          "Action completed Sucessfully",
      };
    }
    return response.Data;
  }

  async getShopItems(): Promise<ShopItemsDTO[]> {
    const uri = `shop/GetShopItems`;
    let result = await this.get<ShopItemsDTO[]>(uri);
    return result ? this.getResponseData(result) : [];
  }

  async getOrders(collectionID: string = ""): Promise<OrderDTO> {
    const uri = `shop/GetOrders?collectionID=${collectionID}`;
    let result = await this.get<OrderDTO>(uri);
    return result ? this.getResponseData(result) : {};
  }

  async getOrderHistory(): Promise<OrderHistoryDTO[]> {
    const uri = `shop/OrderHistory`;
    let result = await this.get<OrderHistoryDTO[]>(uri);
    return result ? this.getResponseData(result) : [];
  }

  async addToCart(data: ShopOrderPostDTO): Promise<boolean> {
    const uri = `shop/AddToCart`;
    let result = await this.post<string, ShopOrderPostDTO>(uri, data);
    if (result) {
      this.getResponseData(result, true);
    }
    return result?.Success!;
  }

  async checkoutCart(): Promise<boolean> {
    const uri = `shop/CheckoutCart`;
    let result = await this.post<string, any>(uri, {});
    if (result) {
      this.getResponseData(result, true);
    }
    return result?.Success!;
  }

  async deleteFromCart(cartItem: CartItemDTO): Promise<boolean> {
    const uri = `shop/RemoveFromCart`;
    let result = await this.post<string, CartItemDTO>(uri, cartItem);
    if (result) {
      this.getResponseData(result, true);
    }
    return result?.Success!;
  }

  async printReciept(session: string = "", term: string = "0", collectionId: string = ""): Promise<FileAttachment> {
    const uri = `payments/reciept?session=${session}&term=${term || 0}&collectionId=${collectionId}`;
    let result = await this.get<FileAttachment>(uri);
    return result ? this.getResponseData(result) : {};
  }
}
